import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Plus, Search, Edit2, ToggleLeft, ToggleRight } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Input } from "shared/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"
import {
  MenuDto,
  MenuUpsertRequest,
  PagedResultDto,
  searchMenus,
  createMenu,
  updateMenu,
  setMenuActive,
  getMenu,
} from "modules/admin/api/menuService"

interface MenuMasterProps {
  currentUser?: { tenantId?: string; name?: string }
}

type FormState = {
  code: string
  label: string
  parentId: string | null
  routePath: string
  externalUrl: string
  icon: string
  sortOrder: number
  isActive: boolean
}

const initialForm: FormState = {
  code: "",
  label: "",
  parentId: null,
  routePath: "",
  externalUrl: "",
  icon: "",
  sortOrder: 1,
  isActive: true,
}

export function MenuMaster({ currentUser }: MenuMasterProps) {
  const tenantId = currentUser?.tenantId ?? DEFAULT_TENANT_ID
  const [items, setItems] = useState<MenuDto[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editing, setEditing] = useState<MenuDto | null>(null)
  const [form, setForm] = useState<FormState>(initialForm)
  const [parentLookup, setParentLookup] = useState<Record<string, MenuDto>>({})

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])

  const loadData = useCallback(
    async (currentPage = 1) => {
      if (!tenantId) return
      setIsLoading(true)
      try {
        const result: PagedResultDto<MenuDto> = await searchMenus({
          tenantId,
          search: searchTerm || undefined,
          page: currentPage,
          pageSize,
        })
        setItems(result.items ?? [])
        setParentLookup((prev) => {
          const merged = { ...prev }
          result.items?.forEach((m) => {
            merged[m.menuId] = m
          })
          return merged
        })
        setTotalItems(result.totalItems ?? result.items.length)
        setPage(result.pageNumber ?? currentPage)
      } catch (err) {
        console.error(err)
        toast.error("โหลดข้อมูลเมนูไม่สำเร็จ")
      } finally {
        setIsLoading(false)
      }
    },
    [tenantId, searchTerm, pageSize]
  )

  useEffect(() => {
    loadData(1)
  }, [loadData])

  const resetForm = () => {
    setForm(initialForm)
    setEditing(null)
  }

  const openCreate = () => {
    resetForm()
    setDialogOpen(true)
  }

  const ensureParentInCache = useCallback(
    async (parentId: string) => {
      if (parentLookup[parentId]) return
      try {
        const parent = await getMenu(parentId)
        setParentLookup((cache) => ({ ...cache, [parent.menuId]: parent }))
      } catch (err) {
        console.warn("ไม่พบ parent menu", err)
      }
    },
    [parentLookup]
  )

  const openEdit = (item: MenuDto) => {
    setEditing(item)
    setForm({
      code: item.code,
      label: item.label,
      parentId: item.parentId ?? null,
      routePath: item.routePath ?? "",
      externalUrl: item.externalUrl ?? "",
      icon: item.icon ?? "",
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    })
    if (item.parentId) {
      ensureParentInCache(item.parentId)
    }
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!tenantId) return
    setIsSubmitting(true)
    try {
      const payload: MenuUpsertRequest = {
        tenantId,
        code: form.code.trim(),
        label: form.label.trim(),
        parentId: form.parentId || null,
        routePath: form.routePath.trim() || null,
        externalUrl: form.externalUrl.trim() || null,
        icon: form.icon.trim() || null,
        sortOrder: form.sortOrder || 1,
        isActive: form.isActive,
      }

      if (editing) {
        await updateMenu(editing.menuId, payload)
        toast.success("อัปเดตเมนูสำเร็จ")
      } else {
        await createMenu(payload)
        toast.success("สร้างเมนูสำเร็จ")
      }
      setDialogOpen(false)
      resetForm()
      loadData(page)
    } catch (err: any) {
      console.error(err)
      const msg = err?.response?.data ?? "บันทึกเมนูไม่สำเร็จ"
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (item: MenuDto) => {
    try {
      await setMenuActive(item.menuId, !item.isActive)
      toast.success("อัปเดตสถานะสำเร็จ")
      loadData(page)
    } catch (err) {
      console.error(err)
      toast.error("อัปเดตสถานะไม่สำเร็จ")
    }
  }

  const parentOptions = useMemo(() => {
    const map: Record<string, MenuDto> = { ...parentLookup }
    items.forEach((m) => {
      map[m.menuId] = map[m.menuId] ?? m
    })
    return Object.values(map).filter((m) => m.menuId !== editing?.menuId)
  }, [items, parentLookup, editing?.menuId])

  const parentLabelLookup = useMemo(() => {
    const map: Record<string, string> = {}
    parentOptions.forEach((m) => {
      map[m.menuId] = m.label
    })
    return map
  }, [parentOptions])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Menu Master</h1>
          <p className="text-gray-600 text-sm">จัดการรายการเมนูของระบบ</p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" /> เพิ่มเมนู
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>เมนูทั้งหมด</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative w-full md:w-80">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
              <Input
                className="pl-9"
                placeholder="ค้นหา code หรือชื่อเมนู"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadData(1)
                }}
              />
            </div>
            <Button variant="outline" onClick={() => loadData(1)} disabled={isLoading}>
              ค้นหา
            </Button>
          </div>

          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Sort</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const parentLabel = item.parentId ? parentLabelLookup[item.parentId] : undefined
                  return (
                    <TableRow key={item.menuId}>
                      <TableCell className="font-medium">{item.code}</TableCell>
                      <TableCell>{item.label}</TableCell>
                      <TableCell>{parentLabel ?? "-"}</TableCell>
                      <TableCell>{item.routePath ?? item.externalUrl ?? "-"}</TableCell>
                      <TableCell>{item.sortOrder}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${item.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="gap-1">
                          <Edit2 className="h-4 w-4" /> แก้ไข
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(item)} className="gap-1">
                          {item.isActive ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-500" />}
                          {item.isActive ? "ปิด" : "เปิด"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                      {isLoading ? "กำลังโหลด..." : "ไม่พบข้อมูล"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>แสดง {items.length} จาก {totalItems} รายการ</span>
            <div className="space-x-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => loadData(page - 1)}>ก่อนหน้า</Button>
              <span>หน้า {page}/{totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => loadData(page + 1)}>ถัดไป</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? "แก้ไขเมนู" : "เพิ่มเมนูใหม่"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            </div>
            <div>
              <Label>ชื่อเมนู</Label>
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
            </div>
            <div>
              <Label>Parent</Label>
              <Select
                value={form.parentId ?? "none"}
                onValueChange={(val) => setForm((f) => ({ ...f, parentId: val === "none" ? null : val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="(None)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(None)</SelectItem>
                  {parentOptions.map((p) => (
                    <SelectItem key={p.menuId} value={p.menuId}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Route Path</Label>
              <Input value={form.routePath} onChange={(e) => setForm((f) => ({ ...f, routePath: e.target.value }))} placeholder="/permit-index" />
            </div>
            <div>
              <Label>External URL</Label>
              <Input value={form.externalUrl} onChange={(e) => setForm((f) => ({ ...f, externalUrl: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <Label>Icon</Label>
              <Input value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} placeholder="lucide icon name" />
            </div>
            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 1 }))}
              />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input
                id="isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              <Label htmlFor="isActive">ใช้งาน</Label>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>ยกเลิก</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? "กำลังบันทึก..." : "บันทึก"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MenuMaster
